import React from 'react';
import { Check, X } from 'lucide-react';
import { Sheet, SheetContent } from '@/shared/widgets/sheet';
import { cn } from '@/shared/utils/cn';
import type { ListingFilter } from '../../domain/models/Filter';

interface PlpFilterSheetV2Props {
  open: boolean;
  filters: ListingFilter[];
  selectedOptions: Record<string, string[]>;
  applyUrl: string | null;
  isLoadingPreview: boolean;
  filtersResetUrl?: string;
  onToggleOption: (category: string, name: string, url: string) => void;
  onApply: () => void;
  onReset: () => void;
  onClose: () => void;
  /** Analytics: fires when a filter item is toggled; receives category + displayName */
  onFilterItemToggle?: (category: string, displayName: string) => void;
  onFilterCategoryExpand?: (category: string) => void;
}

export function PlpFilterSheetV2({
  open,
  filters,
  selectedOptions,
  onToggleOption,
  onApply,
  onReset,
  onClose,
  onFilterItemToggle,
  onFilterCategoryExpand,
}: PlpFilterSheetV2Props) {
  const [activeCategory, setActiveCategory] = React.useState<string>('');

  const openedCategoriesRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (!open) openedCategoriesRef.current.clear();
  }, [open]);

  const totalActiveCount = Object.values(selectedOptions).reduce(
    (sum, names) => sum + names.length,
    0,
  );
  const activeFilter = activeCategory
    ? filters.find((filter) => filter.category === activeCategory)
    : filters[0];

  const handleCategorySelect = (category: string) => {
    setActiveCategory(category);
    if (!openedCategoriesRef.current.has(category)) {
      openedCategoriesRef.current.add(category);
      onFilterCategoryExpand?.(category);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        side="left"
        className="plp-filter-sheet-v2 flex h-[100dvh] max-h-[100dvh] w-full max-w-none flex-col gap-0 rounded-none bg-white p-0 sm:max-w-none"
        showCloseButton={false}
      >
        <header className="flex h-[72px] shrink-0 items-center justify-between border-b border-[#D8DCE3] px-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Close filters"
              className="flex h-8 w-8 items-center justify-center text-[#111827]"
              onClick={onClose}
            >
              <X className="h-5 w-5" strokeWidth={1.8} />
            </button>
            <h2 className="font-primary text-[21px] font-semibold text-[#092B67]">Filters</h2>
            <span className="flex min-w-10 items-center justify-center rounded-md bg-[linear-gradient(90deg,#F1F9F5_0%,#FFF4F5_100%)] px-2 py-1 text-sm text-[#5B8B68]">
              {totalActiveCount}
            </span>
          </div>
          <button
            type="button"
            className="rounded-xl border border-[#BFD8FF] px-4 py-2 text-sm font-medium text-[#092B67]"
            onClick={onReset}
          >
            Reset
          </button>
        </header>

        <div className="flex min-h-0 flex-1">
          <nav aria-label="Filter categories" className="w-[38%] shrink-0 overflow-y-auto pb-[calc(84px+max(20px,env(safe-area-inset-bottom)))] border-r border-[#E4E7EC] bg-white">
            {filters.map((filter) => {
              const selectedCount = selectedOptions[filter.category]?.length ?? 0;
              const isActive = filter.category === activeFilter?.category;

              return (
                <button
                  key={filter.category}
                  type="button"
                  className={cn(
                    'flex min-h-[62px] w-full items-center justify-between border-b border-[#E4E7EC] px-5 py-3 text-left text-sm text-[#092B67]',
                    isActive && 'bg-[#F0F5FC] font-semibold',
                  )}
                  onClick={() => handleCategorySelect(filter.category)}
                >
                  <span className="truncate pr-2">{filter.category}</span>
                  {selectedCount > 0 && (
                    <span className="flex min-w-10 shrink-0 items-center justify-center rounded-md bg-[linear-gradient(90deg,#F1F9F5_0%,#FFF4F5_100%)] px-2 py-1 text-sm font-normal text-[#5B8B68]">
                      {selectedCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="min-w-0 flex-1 overflow-y-auto pb-[calc(84px+max(20px,env(safe-area-inset-bottom)))] bg-white">
            {activeFilter?.items.map((item) => {
              const isSelected = (selectedOptions[activeFilter.category] ?? []).includes(item.name);

              return (
                <button
                  key={item.name}
                  type="button"
                  className={cn(
                    'flex min-h-[52px] w-full items-center justify-between gap-3 px-6 py-3 text-left text-sm text-[#092B67]',
                    isSelected && 'bg-[#F0F5FC]',
                  )}
                  onClick={() => {
                    onFilterItemToggle?.(activeFilter.category, item.displayName);
                    onToggleOption(activeFilter.category, item.name, item.url);
                  }}
                >
                  <span>
                    {item.displayName}
                    {item.count > 0 && <span className="font-normal"> ({item.count})</span>}
                  </span>
                  {isSelected && <Check className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />}
                </button>
              );
            })}
          </div>
        </div>

        <footer className="absolute inset-x-0 bottom-0 z-10 rounded-t-[32px] border-t border-black/10 bg-white/5 backdrop-blur-xl px-6 pb-[max(20px,env(safe-area-inset-bottom))] pt-4 shadow-[0_-8px_24px_rgba(15,23,42,0.08)]">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className="h-12 rounded-xl border border-[#BFD8FF] bg-white text-base font-medium text-[#092B67]"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="h-12 rounded-xl bg-[#123B78] text-base font-medium text-white"
              onClick={onApply}
            >
              Apply
            </button>
          </div>
        </footer>
      </SheetContent>
    </Sheet>
  );
}
